import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  signOut,
  onAuthStateChanged,
  NextOrObserver,
  User,
  ErrorFn,
  CompleteFn,
  Unsubscribe,
  Auth,
  signInWithRedirect,
  getRedirectResult
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  addDoc,
  serverTimestamp,
  Timestamp,
  limit,
  setDoc,
  Firestore, 
  DocumentData, 
  FirestoreDataConverter,
  DocumentReference,
  CollectionReference,
  Query,
  DocumentSnapshot,
  QuerySnapshot,
  CACHE_SIZE_UNLIMITED,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getAnalytics, isSupported } from 'firebase/analytics';
import { toast } from 'sonner';
import { FirebaseApp } from 'firebase/app';

// Decide which implementations to use
const USE_MOCKS = false;

// Initialize Firebase with the correct production configuration
const app = initializeApp({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
});

// Initialize Firebase services with enhanced configuration
const auth: Auth = getAuth(app);
const storage = getStorage(app);

// Initialize Firestore with new cache settings for multi-tab persistence
const db = initializeFirestore(app, {
  cacheSizeBytes: CACHE_SIZE_UNLIMITED,
  experimentalAutoDetectLongPolling: true,
  cache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});

// Enhanced network state monitoring
let isOnline = navigator.onLine;
let networkStatusListeners: Array<(status: boolean) => void> = [];

const notifyNetworkStatusListeners = (status: boolean) => {
  networkStatusListeners.forEach(listener => listener(status));
};

window.addEventListener('online', () => {
  isOnline = true;
  console.log('Network connection restored');
  notifyNetworkStatusListeners(true);
  toast.success('Network connection restored');
});

window.addEventListener('offline', () => {
  isOnline = false;
  console.log('Network connection lost');
  notifyNetworkStatusListeners(false);
  toast.error('Network connection lost');
});

// Enhanced Firestore error handling with retry logic
const handleFirestoreError = async (error: any, retryOperation?: () => Promise<any>) => {
  console.error('Firestore error:', error);
  
  if (!isOnline) {
    toast.error('You are offline. Please check your internet connection.');
    return;
  }

  switch (error.code) {
    case 'permission-denied':
      toast.error('You do not have permission to perform this action.');
      break;
    case 'unavailable':
      toast.error('The service is currently unavailable. Please try again later.');
      if (retryOperation) {
        try {
          await retryOperation();
        } catch (retryError) {
          console.error('Retry operation failed:', retryError);
        }
      }
      break;
    case 'resource-exhausted':
      toast.error('The service has exceeded its quota. Please try again later.');
      break;
    case 'failed-precondition':
      toast.error('The operation was rejected because the system is not in a state required for the operation\'s execution.');
      break;
    case 'aborted':
      toast.error('The operation was aborted.');
      break;
    case 'out-of-range':
      toast.error('The operation was attempted past the valid range.');
      break;
    case 'unimplemented':
      toast.error('The operation is not implemented or not supported/enabled.');
      break;
    case 'internal':
      toast.error('Internal errors. This means some invariants expected by the underlying system have been broken.');
      break;
    default:
      toast.error('An error occurred. Please try again.');
  }
};

// Export enhanced Firestore functions with error handling and retry logic
export const enhancedFirestore = {
  collection: (path: string) => {
    try {
      return collection(db, path);
    } catch (error) {
      handleFirestoreError(error);
      throw error;
    }
  },
  query: (collectionRef: CollectionReference, ...queryConstraints: QueryConstraint[]) => {
    try {
      return query(collectionRef, ...queryConstraints);
    } catch (error) {
      handleFirestoreError(error);
      throw error;
    }
  },
  getDocs: async (query: Query) => {
    const operation = async () => {
      try {
        return await getDocs(query);
      } catch (error) {
        await handleFirestoreError(error, () => getDocs(query));
        throw error;
      }
    };
    return operation();
  },
  // Add other Firestore functions as needed
};

// Export network status utilities
export const networkUtils = {
  isOnline: () => isOnline,
  addNetworkStatusListener: (listener: (status: boolean) => void) => {
    networkStatusListeners.push(listener);
    return () => {
      networkStatusListeners = networkStatusListeners.filter(l => l !== listener);
    };
  }
};

// Set up Google Authentication provider
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Set persistent authentication to keep user logged in
try {
  setPersistence(auth, browserLocalPersistence)
    .then(() => console.log('Auth persistence set to LOCAL'))
    .catch(error => {
      console.error('Error setting auth persistence:', error);
    });
} catch (error) {
  console.error('Fatal error while setting auth persistence:', error);
}

// Initialize Analytics conditionally
let analytics = null;
isSupported()
  .then(supported => {
    if (supported) {
      analytics = getAnalytics(app);
      console.log('Firebase Analytics initialized');
    }
  })
  .catch(error => console.warn('Analytics not supported:', error));

// Authentication functions
export const signInWithEmail = async (email: string, password: string) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result;
  } catch (error) {
    console.error('Email sign-in error:', error);
    throw error;
  }
};

export const signUpWithEmail = async (email: string, password: string) => {
  try {
    return await createUserWithEmailAndPassword(auth, email, password);
  } catch (error) {
    console.error('Email sign-up error:', error);
    throw error;
  }
};

export const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  
  try {
    // First try popup
    return await signInWithPopup(auth, provider);
  } catch (error: any) {
    if (error.code === 'auth/popup-blocked') {
      // If popup is blocked, fall back to redirect
      console.log('Popup blocked, falling back to redirect');
      await signInWithRedirect(auth, provider);
      // The redirect result will be handled by the auth state listener
      return null;
    }
    throw error;
  }
};

// Add handler for redirect result
export const handleRedirectResult = async () => {
  try {
    const result = await getRedirectResult(auth);
    if (result) {
      return result;
    }
  } catch (error) {
    console.error('Error handling redirect result:', error);
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Sign-out error:', error);
    throw error;
  }
};

// Export Firebase services and functions
export {
  app,
  auth,
  db,
  storage,
  analytics,
  googleProvider,
  // Firestore functions
  collection,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
  limit
}; 